// src/portal-app/shell/GlassNavBar.js
//
// GlassNavBar (W2 Task 3) — the frosted header every PUSHED screen wears,
// built to the approved mockup's .topbar / .navrow / .icon-btn spec
// (docs/mockups/f12b/manager-liquid-glass.html) minus the simulated iOS
// status bar: the real device draws that, we honour it with --safe-top.
//
// OWNERSHIP: screens do NOT render this themselves. NavStackHost
// (src/portal-app/nav/NavStack.js) mounts one per pushed screen with the
// title resolved from ROUTES meta and onBack wired to pop() — on iOS this
// header is the ONLY back affordance, so it cannot be left to screen
// authors to remember. This file is purely presentational.
//
//   <GlassNavBar
//       title      // string (already translated) — required
//       subtitle   // string, optional — small muted line under the title
//       onBack     // fn — back button renders only when provided
//       actions    // [{ id, label, icon, onPress }], optional trailing
//                  //   44pt icon buttons; `label` (translated) is the
//                  //   accessible name; `icon` an inline SVG node
//   />
//
// Surface: the Liquid-Glass recipe via glass() — never re-implemented —
// reshaped to a full-width bar (radius 0, hairline bottom edge, no depth
// shadow; content scrolls UNDER it so the blur frosts for real). Position
// contract: absolute to the top of a position:relative screen container
// (NavStackHost's .pnav-screen).
//
// A11y/RTL: back target ≥44pt with its accessible name from t('back');
// the chevron mirrors under RTL; the title truncates with ellipsis;
// hover/press motion is disabled under prefers-reduced-motion. Tokens
// only — translucency via color-mix(in srgb, var(--c-*) N%, transparent);
// the rgba slate depth-shadow is the approved recipe exception
// (see LiquidGlass.js). Inline SVG icons only — no emoji.
import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { glass } from './LiquidGlass';

// Height of the nav row (mockup .navrow: 52). NavStack's .pnav-body uses it
// to pad the scroll body so the header never obscures content.
export const NAV_BAR_HEIGHT = 52;

const CSS = `
.gnav{inset-block-start:0;inset-inline:0;z-index:2;box-sizing:border-box;
  padding-block-start:var(--safe-top, 0px);padding-inline:12px;}
.gnav-row{min-height:${NAV_BAR_HEIGHT}px;display:flex;align-items:center;gap:10px;}
/* icon buttons — mockup .icon-btn grown to the 44pt minimum touch target */
.gnav-back,.gnav-act{flex:none;min-width:44px;min-height:44px;border-radius:14px;
  display:inline-flex;align-items:center;justify-content:center;cursor:pointer;
  padding:0;font-family:inherit;-webkit-tap-highlight-color:transparent;
  background:color-mix(in srgb,var(--c-surface) 42%,transparent);
  border:1px solid color-mix(in srgb,var(--c-surface) 50%,transparent);
  box-shadow:0 2px 8px rgba(15,23,42,.08),inset 0 1px 0 color-mix(in srgb,var(--c-surface) 60%,transparent);
  color:var(--c-ink);transition:transform .15s ease;}
.gnav-act{color:var(--c-muted);}
.gnav-back:hover,.gnav-act:hover{transform:scale(1.05);}
.gnav-back:active,.gnav-act:active{transform:scale(.95);}
.gnav-back svg{width:22px;height:22px;}
.gnav-act svg{width:18px;height:18px;}
.gnav-titles{flex:1;min-width:0;}
.gnav-title{margin:0;font-size:17px;font-weight:800;letter-spacing:-.3px;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.gnav-sub{margin:1px 0 0;font-size:11.5px;font-weight:600;color:var(--c-muted);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
@media (prefers-reduced-motion: reduce){
  .gnav-back,.gnav-act,.gnav-back:hover,.gnav-act:hover,.gnav-back:active,.gnav-act:active{transition:none;transform:none;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'gnav-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

export default function GlassNavBar({ title, subtitle, onBack, actions }) {
    ensureCss();
    const { t, isRTL } = useLanguage();

    return (
        <header
            className="gnav"
            data-testid="glass-nav-bar"
            // Liquid-Glass recipe reshaped to a bar: full-width (radius 0),
            // hairline bottom edge instead of the card border, no depth
            // shadow. position:absolute overrides the recipe's `relative`
            // (placement insets live in .gnav).
            style={glass({
                position: 'absolute',
                borderRadius: 0,
                border: 'none',
                borderBlockEnd:
                    '1px solid color-mix(in srgb, var(--c-surface) 45%, transparent)',
                boxShadow: 'none',
            })}
        >
            <div className="gnav-row">
                {onBack ? (
                    <button
                        type="button"
                        className="gnav-back"
                        aria-label={t('back', {}, 'Back')}
                        onClick={onBack}
                    >
                        {/* chevron points "back": mirrored under RTL */}
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            style={isRTL ? { transform: 'scaleX(-1)' } : undefined}
                        >
                            <path d="M14.5 5.5L8 12l6.5 6.5" />
                        </svg>
                    </button>
                ) : null}
                <div className="gnav-titles">
                    <h1 className="gnav-title">{title}</h1>
                    {subtitle ? <p className="gnav-sub">{subtitle}</p> : null}
                </div>
                {(actions || []).map((action) => (
                    <button
                        key={action.id}
                        type="button"
                        className="gnav-act"
                        aria-label={action.label}
                        title={action.label}
                        onClick={action.onPress}
                    >
                        {action.icon}
                    </button>
                ))}
            </div>
        </header>
    );
}
